import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TrustSignalsBar from "../components/TrustSignalsBar";
import { api, SERVICE_AREAS, BRAND } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import EstimateCalculator from "../components/EstimateCalculator";
import AddressAutocomplete from "../components/AddressAutocomplete";
import GateWidthPicker from "../components/GateWidthPicker";
import YardSizeSelector from "../components/YardSizeSelector";
import PaymentMethodStep from "../components/PaymentMethodStep";
import AddServiceDialog from "../components/AddServiceDialog";
import CustomerPropertyPreview from "../components/CustomerPropertyPreview";
import { CheckCircle2, Loader2, Phone, Banknote, Clock, MapPin, Plus, Trash2, Leaf } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const TIMES = ["Morning (7am-11am)", "Midday (11am-2pm)", "Afternoon (2pm-6pm)"];

// Human-readable frequency labels used by both the sidebar summary and the
// Step 3 (Confirm Booking & Pay) service list. Kept as a single source of
// truth so customer-facing copy stays consistent everywhere.
const FREQ_LABELS = {
  one_time: "One-time", weekly: "Weekly", biweekly: "Every 2 weeks",
  every_3_weeks: "Every 3 weeks", every_4_weeks: "Every 4 weeks",
  monthly: "Monthly", every_6_weeks: "Every 6 weeks",
  every_2_months: "Every 2 months", every_3_months: "Every 3 months",
  every_6_months: "Every 6 months", yearly: "Yearly", custom: "Custom",
};
const freqLabel = (f) => FREQ_LABELS[f] || (f || "One-time");

export default function Book() {
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [estimate, setEstimate] = useState(null);
  const [additional, setAdditional] = useState([]);  // array of addon items
  const [addOpen, setAddOpen] = useState(false);

  // iter116 — When landing from Estimate's "Add More Services" button
  // (?add=1) we auto-open the AddServiceDialog as soon as we hit Step 2
  // so the customer never has to hunt for the button. We consume the flag
  // after opening so re-renders / dialog closes don't re-trigger it.
  useEffect(() => {
    if (step === 2 && params.get("add") === "1") {
      const t = setTimeout(() => {
        setAddOpen(true);
        const next = new URLSearchParams(params);
        next.delete("add");
        setParams(next, { replace: true });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [step, params, setParams]);
  const [property, setProperty] = useState(null); // {place_id, formatted_address, lat, lng, acres, square_feet, source}
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    area: SERVICE_AREAS[0],
    preferred_date: "",
    preferred_time: TIMES[0],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [resumeTokenState, setResumeTokenState] = useState(() => params.get("resume") || "");
  const [attemptIdState, setAttemptIdState] = useState("");

  // iter255 (2026-08-24) — Multi-Property (Phase 1). Load the customer's
  // saved properties so Step 2 can present a "Which property?" picker
  // instead of forcing them to retype an address they've already saved.
  // Each pick pre-fills the address, sqft, and yard area from the chosen
  // property and stamps `property_id` on the booking so admin can see
  // which property this visit is for.
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/me/properties")
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.properties || [];
        setProperties(list);
        if (!selectedPropertyId) {
          const def = list.find((p) => p.is_default) || list[0];
          if (def) {
            setSelectedPropertyId(def.property_id);
            setField("address", def.address || "");
            const area = SERVICE_AREAS.find((a) =>
              (def.formatted_address || def.address || "").toLowerCase().includes(a.toLowerCase())
            );
            if (area) setField("area", area);
            if (def.square_feet && !property?.square_feet) {
              setProperty({
                formatted_address: def.formatted_address || def.address,
                square_feet: def.square_feet,
                mowable_sqft: def.mowable_sqft || null,
                place_id: def.place_id || null,
                lat: def.lat || null,
                lng: def.lng || null,
                source: def.property_source || "saved_property",
              });
            }
          }
        }
      })
      .catch(() => setProperties([]));
    return () => { cancelled = true; };
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const chooseProperty = (p) => {
    setSelectedPropertyId(p.property_id);
    setField("address", p.address || "");
    const area = SERVICE_AREAS.find((a) =>
      (p.formatted_address || p.address || "").toLowerCase().includes(a.toLowerCase())
    );
    if (area) setField("area", area);
    setProperty({
      formatted_address: p.formatted_address || p.address,
      square_feet: p.square_feet,
      mowable_sqft: p.mowable_sqft || null,
      place_id: p.place_id || null,
      lat: p.lat || null,
      lng: p.lng || null,
      source: p.property_source || "saved_property",
    });
    // Recompute the estimate for this property's sqft so the price
    // shown at checkout is the property-specific price.
    const detected = Number(p.mowable_sqft || p.square_feet || 0);
    if (detected > 0) recalcWithSqft(detected);
  };

  // iter61 — declared BEFORE the auto-save useEffect below to avoid the TDZ
  // ReferenceError that crashed the /book route pre-fix.
  const defaultService = params.get("service") || "lawn_mowing";
  const defaultFrequency = params.get("frequency") || (params.get("recurring") ? "weekly" : "biweekly");
  const defaultYardArea = params.get("yard_area") || "both";
  const defaultSqft = parseInt(params.get("sqft") || "2000", 10) || 2000;

  // iter80/81 — When arriving from /estimate → Go Pay Now the caller has
  // already computed the full estimate and stashed it into sessionStorage
  // under key `jdsn_pending_estimate`. Prefer that payload (no recalc,
  // guarantees the price shown on /estimate === price shown here === price
  // the server will re-compute from identical inputs on /api/bookings). If
  // sessionStorage is empty (deep link / hard refresh / shared URL), fall
  // back to recalculating from the URL query params which now carry the
  // FULL input set (grass_height, grass_coverage, area_already_scaled +
  // optional flags). If /calculate fails we stay on Step 1 so the customer
  // can adjust — never navigate backward.
  useEffect(() => {
    const hasFullEstimate =
      params.get("service") && params.get("sqft") && params.get("frequency") && params.get("yard_area");
    if (!hasFullEstimate) return;
    if (estimate) return;
    if (params.get("resume")) return;

    const optsFromUrl = {
      heavy_debris:    params.get("heavy_debris") === "1",
      severe_debris:   params.get("severe_debris") === "1",
      corner_lot:      params.get("corner_lot") === "1",
      difficult_labor: params.get("difficult_labor") === "1",
    };
    const grassHeightUrl    = params.get("grass_height") || "under_9";
    const grassCoverageUrl  = params.get("grass_coverage") || "full";
    const areaAlreadyScaledUrl = params.get("area_already_scaled") === "1";

    // (a) Try the sessionStorage handoff first — the estimate calc is
    // already trustworthy, do NOT recalc.
    try {
      const raw = sessionStorage.getItem("jdsn_pending_estimate");
      if (raw) {
        const stashed = JSON.parse(raw);
        const matches =
          stashed?.serviceKey === defaultService &&
          Number(stashed?.sqft) === Number(defaultSqft) &&
          stashed?.frequency === defaultFrequency &&
          stashed?.yardArea === defaultYardArea &&
          // Recent (<20 min) — protects against very stale handoffs from a
          // long-abandoned tab
          typeof stashed?.savedAt === "number" &&
          (Date.now() - stashed.savedAt) < 20 * 60 * 1000 &&
          stashed?.calc && typeof stashed.calc.total === "number" &&
          // iter239 — Never trust a $0 stash. If the homepage handoff
          // couldn't compute a firm price (e.g. free-text address range
          // quote) it must fall through to the URL-recompute path below
          // so /api/estimates/calculate produces the real number.
          Number(stashed.calc.total) > 0;
        if (matches) {
          setEstimate({
            serviceKey: stashed.serviceKey,
            sqft: stashed.sqft,
            opts: stashed.opts || optsFromUrl,
            frequency: stashed.frequency,
            yardArea: stashed.yardArea,
            calc: stashed.calc,
            areaAlreadyScaled: !!stashed.areaAlreadyScaled,
            grassHeight: stashed.grassHeight || grassHeightUrl,
            grassCoverage: stashed.grassCoverage || grassCoverageUrl,
            overgrownOverride: stashed.overgrownOverride || "",
            gate_width: stashed.gate_width || params.get("gate_width") || "",
          });
          // iter90: Also restore the verified property + prefill the
          // address field so Step 2 shows the satellite preview +
          // "Property Verified" badge immediately AND no address input
          // ever fires onPlace again for the same address. This is the
          // "one address lookup → one property calculation → one locked
          // price → checkout" spec — no second parcel lookup on Step 2.
          if (stashed.property && stashed.property.formatted_address) {
            setProperty(stashed.property);
            setField("address", stashed.property.formatted_address);
          }
          setStep(2);
          window.scrollTo({ top: 0, behavior: "smooth" });
          // iter146 — KEEP the stash until the booking is actually
          // submitted (see submitBooking). Previously we removed it here,
          // which meant a Refresh on Book.jsx wiped the locked calc and
          // fell through to a URL-only recompute that omitted
          // mowable_sqft → different price than the Estimate showed.
          return;
        }
      }
    } catch { /* JSON parse or sessionStorage error — fall through to URL recalc */ }

    // (b) No stash → deep link / refresh path. Recompute with the FULL
    // input set from URL params so the server-side calc matches exactly.
    let cancelled = false;
    (async () => {
      try {
        // iter146 — pull mowable_sqft from the URL (Estimate page writes it
        // when navigating). If missing, the recompute will fall back to
        // square_feet which is the total lot; that would produce a DIFFERENT
        // price than the Estimate page showed for lawn_mowing (which uses
        // mowable_sqft). Refresh-on-Book should ALWAYS reproduce the
        // Estimate price exactly.
        const mowableFromUrl = params.get("mowable_sqft");
        const mowable_sqft = mowableFromUrl ? parseInt(mowableFromUrl, 10) : null;
        const { data: calc } = await api.post("/estimates/calculate", {
          service_key: defaultService,
          square_feet: defaultSqft,
          mowable_sqft: Number.isFinite(mowable_sqft) && mowable_sqft > 0 ? mowable_sqft : null,
          ...optsFromUrl,
          frequency: defaultFrequency,
          yard_area: defaultYardArea,
          area_already_scaled: areaAlreadyScaledUrl,
          grass_height: grassHeightUrl,
          grass_coverage: grassCoverageUrl,
          overgrown_override_pct: null,
        });
        if (cancelled) return;
        setEstimate({
          serviceKey: defaultService,
          sqft: defaultSqft,
          opts: optsFromUrl,
          frequency: defaultFrequency,
          yardArea: defaultYardArea,
          calc,
          areaAlreadyScaled: areaAlreadyScaledUrl,
          grassHeight: grassHeightUrl,
          grassCoverage: grassCoverageUrl,
          overgrownOverride: "",
          gate_width: params.get("gate_width") || "",
        });
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Could not load your estimate. Please review the details below.");
      }
    })();
    return () => { cancelled = true; };
  }, [defaultService, defaultSqft, defaultFrequency, defaultYardArea]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data.services || []));
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  // iter235 (2026-03-02) — When arriving from the homepage quote form
  // (Home.jsx → "Continue to book"), the customer has already given us
  // their address, name, phone, email, service, frequency, and price.
  // Restore all of that from sessionStorage + URL params AND, if
  // `?skip_to_pay=1` is set, jump straight to step 3 (Confirm & Pay)
  // without forcing them back through the quote flow.
  useEffect(() => {
    if (!params.get("skip_to_pay")) return;
    // Restore contact prefill from sessionStorage first, URL params as fallback.
    let contact = null;
    try {
      const raw = sessionStorage.getItem("jdsn_pending_contact");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.savedAt && (Date.now() - p.savedAt) < 20 * 60 * 1000) {
          contact = p;
        }
      }
    } catch { /* ignore */ }
    const nameQ  = contact?.name  ?? params.get("name")  ?? "";
    const emailQ = contact?.email ?? params.get("email") ?? "";
    const phoneQ = contact?.phone ?? params.get("phone") ?? "";
    const addrQ  = contact?.address ?? params.get("addr") ?? "";
    const dateQ  = contact?.preferred_date ?? params.get("preferred_date") ?? "";
    setForm((f) => ({
      ...f,
      name:  f.name  || nameQ,
      email: f.email || emailQ,
      phone: f.phone || phoneQ,
      address: f.address || addrQ,
      preferred_date: f.preferred_date || dateQ,
    }));
    // iter238 — Only jump straight to step 3 if we ALSO have a
    // preferred_date; otherwise land on step 2 so the customer can pick
    // one (POST /api/bookings now rejects empty preferred_date with 422).
    const hasContact = (nameQ || "").trim() && (emailQ || "").trim() && (phoneQ || "").trim();
    const hasDate = (dateQ || "").trim();
    if (estimate && hasContact && hasDate) {
      setStep(3);
      const next = new URLSearchParams(params);
      next.delete("skip_to_pay");
      setParams(next, { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (estimate && hasContact && !hasDate) {
      setStep(2);
      const next = new URLSearchParams(params);
      next.delete("skip_to_pay");
      setParams(next, { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // If estimate isn't ready yet, the earlier restore useEffect will
    // set it — this hook re-runs and promotes us to step 3.
  }, [params, estimate, setParams]);

  // iter173 — Customer Wallet. If the signed-in customer already has a
  // saved card + AutoPay enabled, POST /bookings will auto-attach the
  // wallet (payment_status=autopay_scheduled) and we can skip Stripe
  // Checkout entirely. Pre-fetch the wallet status so the payment step
  // can show a one-tap "Book with saved card" CTA instead of a fresh
  // Stripe redirect.
  const [wallet, setWallet] = useState(null);
  useEffect(() => {
    if (!user) { setWallet(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/saved-card");
        if (cancelled) return;
        if (data?.has_card && data?.autopay_enabled && !data?.autopay_admin_disabled) {
          setWallet({
            brand: data.card_brand || "",
            last4: data.card_last4 || "",
            autopay: true,
          });
        } else {
          setWallet(null);
        }
      } catch { /* ignore — customer likely has no card */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // iter61 — Hydrate from ?resume=TOKEN so returning customers land exactly
  // where they stopped.
  useEffect(() => {
    if (!resumeTokenState) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/bookings/resume/${resumeTokenState}`);
        if (cancelled || !data) return;
        setAttemptIdState(data.attempt_id || "");
        setForm((f) => ({
          ...f,
          name: data.name || f.name,
          email: data.email || f.email,
          phone: data.phone || f.phone,
          address: data.address || data.formatted_address || f.address,
          preferred_date: data.preferred_date || f.preferred_date,
          preferred_time: data.preferred_time_window ? String(data.preferred_time_window)
                                                     .replace(/^./, (c) => c.toUpperCase()) : f.preferred_time,
        }));
        if (data.formatted_address) {
          setProperty({ formatted_address: data.formatted_address, square_feet: data.square_feet });
        }
      } catch (_e) { /* invalid or expired — silently ignore */ }
    })();
    return () => { cancelled = true; };
  }, [resumeTokenState]);

  // iter61 — Auto-save booking progress every ~1.2s of no changes so we can
  // recover abandoned carts. Only when we have at least an email OR phone
  // OR the customer has already progressed past step 1.
  useEffect(() => {
    const hasIdentity = !!(form.email || form.phone || user?.email);
    if (!hasIdentity && step <= 1) return;   // don't spam anonymous drafts
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/bookings/progress", {
          attempt_id: attemptIdState || undefined,
          resume_token: resumeTokenState || undefined,
          email: form.email || user?.email || null,
          phone: form.phone || null,
          name: form.name || user?.name || null,
          service_key: estimate?.serviceKey || defaultService,
          service_name: services.find((s) => s.key === (estimate?.serviceKey || defaultService))?.name || null,
          address: form.address || null,
          formatted_address: property?.formatted_address || null,
          frequency: estimate?.frequency || defaultFrequency,
          preferred_date: form.preferred_date || null,
          preferred_time_window: (form.preferred_time || "").toLowerCase() || null,
          square_feet: estimate?.sqft || property?.square_feet || null,
          yard_area: estimate?.yardArea || null,
          price: estimate?.calc?.total || null,
          step: ["service", "size", "schedule", "contact", "review"][Math.max(0, step - 1)],
          source: "web",
        });
        if (data?.attempt_id && !attemptIdState) setAttemptIdState(data.attempt_id);
        if (data?.resume_token && !resumeTokenState) setResumeTokenState(data.resume_token);
      } catch (_e) { /* non-fatal — customer can still finish */ }
    }, 1200);
    return () => clearTimeout(t);
  // Fire whenever anything material changes
  }, [step, form.name, form.email, form.phone, form.address, form.preferred_date,
      form.preferred_time, estimate?.serviceKey, estimate?.sqft, estimate?.frequency,
      estimate?.yardArea, estimate?.calc?.total, property?.formatted_address, property?.square_feet,
      user?.email, user?.name, attemptIdState, resumeTokenState, defaultFrequency, defaultService, services]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleContinue = ({ serviceKey, sqft, opts, frequency, yardArea, areas, calc, areaAlreadyScaled, grassHeight, grassCoverage, overgrownOverride, gate_width }) => {
    setEstimate({ serviceKey, sqft, opts, frequency, yardArea, areas: areas || [], calc, areaAlreadyScaled: !!areaAlreadyScaled, grassHeight: grassHeight || "under_9", grassCoverage: grassCoverage || "full", overgrownOverride: overgrownOverride || "", gate_width: gate_width || "" });
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Re-run the price calculation with a new sqft and update the estimate state
  const recalcWithSqft = async (newSqft) => {
    if (!estimate) return;
    try {
      const overrideNum = parseFloat(estimate.overgrownOverride);
      const { data: calc } = await api.post("/estimates/calculate", {
        service_key: estimate.serviceKey,
        square_feet: newSqft,
        // iter146 — keep mowable_sqft in sync so the recalc matches the
        // Estimate page's numbers exactly.
        mowable_sqft: property?.mowable_sqft ?? newSqft,
        ...estimate.opts,
        frequency: estimate.frequency,
        yard_area: estimate.yardArea,
        area_already_scaled: estimate.areaAlreadyScaled || false,
        grass_height: estimate.grassHeight || "under_9",
        grass_coverage: estimate.grassCoverage || "full",
        overgrown_override_pct: !isNaN(overrideNum) && overrideNum >= 0 ? overrideNum / 100 : null,
      });
      setEstimate({ ...estimate, sqft: newSqft, calc });
      toast.success(`Updated estimate to ${newSqft.toLocaleString()} sqft · $${calc.total.toFixed(2)}`);
    } catch {
      toast.error("Could not update estimate. Try again.");
    }
  };

  const submitBooking = async (paymentMethod, opts = {}) => {
    if (!estimate) return null;
    setSubmitting(true);
    try {
      const payload = {
        service_key: estimate.serviceKey,
        name: form.name, email: form.email, phone: form.phone, address: form.address, area: form.area,
        // iter150 — Twilio A2P 10DLC consent (defaults to true when checkbox
        // untouched, per the checkbox render on Book step 2). Backend stores
        // this alongside the booking for compliance audit.
        sms_consent: form.sms_consent !== false,
        sms_consent_at: (form.sms_consent !== false) ? new Date().toISOString() : null,
        square_feet: estimate.sqft,
        ...estimate.opts,
        frequency: estimate.frequency || "biweekly",
        yard_area: estimate.yardArea || "both",
        areas: estimate.areas || [],  // iter126 — canonical multi-area selection
        area_already_scaled: estimate.areaAlreadyScaled || false,
        grass_height: estimate.grassHeight || "under_9",
        grass_coverage: estimate.grassCoverage || "full",
        overgrown_override_pct: (() => {
          const n = parseFloat(estimate.overgrownOverride);
          return !isNaN(n) && n >= 0 ? n / 100 : null;
        })(),
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        notes: form.notes,
        place_id: property?.place_id || null,
        formatted_address: property?.formatted_address || form.address,
        lat: property?.lat || null,
        lng: property?.lng || null,
        acres: property?.acres || null,
        property_source: property?.source || (property ? "google" : "manual"),
        // iter41 — Mowable area + confidence (forwarded from /property/lookup)
        total_lot_sqft: property?.total_lot_sqft ?? property?.square_feet ?? null,
        house_sqft: property?.house_sqft ?? property?.building_footprint_sqft ?? null,
        mowable_sqft: property?.mowable_sqft ?? null,
        mowable_confidence: property?.confidence || (property?.square_feet ? "medium" : "low"),
        mowable_confidence_pct: property?.confidence_pct ?? null,
        payment_method: paymentMethod,
        // iter82: Save-card is now automatic — every Stripe checkout enables
        // off-session billing for future recurring visits. No customer opt-in
        // required.
        save_card_for_future: paymentMethod === "stripe",
        // iter255 — Multi-Property (Phase 1). Stamp the selected property
        // so admin can see which property this booking is for. Payment
        // methods stay under the single customer account (Stripe / AutoPay).
        property_id: selectedPropertyId || null,
        additional_services: additional.map((a) => ({
          service_key: a.service_key,
          square_feet: a.square_feet || 0,
          frequency: a.frequency || "one_time",
          yard_area: a.yard_area || "both",
          price: a.price,
          quote_low: a.quote_low ?? null,
          quote_high: a.quote_high ?? null,
          label: a.label || "",
          // iter107 — pass advanced-addon config to backend so it re-prices
          // + persists the itemized breakdown for admin visibility.
          options: a.options || null,
          line_items: a.line_items || null,
          // iter108 — per-service independent recurring schedule.
          schedule: a.schedule || null,
        })),
        // iter101 — set true only when the customer clicked "Continue anyway"
        // in the duplicate-warning modal.
        confirm_duplicate: !!opts.confirm_duplicate,
        // iter103 — attach marketplace referral captured by the splash so
        // the guest-created user is attributed (source: angi | thumbtack).
        referral_source: (() => {
          try { return sessionStorage.getItem("jdsn_referral_source") || null; }
          catch { return null; }
        })(),
        // iter135 — Gate width (mower access)
        gate_width: estimate.gate_width || null,
        // iter142 — Price lock: pass the EXACT total the customer saw on the
        // Continue page so the backend accepts it as the authoritative price
        // (guarded by a server-side tolerance check). This guarantees the
        // number the customer confirms is what gets billed, invoiced, and
        // shown in the portal / admin — never a different price.
        agreed_price: (() => {
          const primary = Number(estimate?.calc?.total || 0);
          const addons = additional.reduce((s, a) => s + Number(a?.price || 0), 0);
          const total = Math.round((primary + addons) * 100) / 100;
          return total > 0 ? total : null;
        })(),
      };
      const { data } = await api.post("/bookings", payload);

      // iter101 — Duplicate detected. Show the warning modal instead of
      // proceeding to Stripe. On "Continue anyway" the client re-invokes
      // submitBooking with opts.confirm_duplicate=true.
      if (data && data.requires_confirmation && data.duplicate_warning) {
        const dw = data.duplicate_warning;
        const msg =
          `It looks like you already have a booking:\n\n` +
          `${dw.existing_service} on ${dw.existing_date} at ${dw.existing_time}\n` +
          `Status: ${dw.existing_status} · Payment: ${dw.existing_payment_status}\n\n` +
          `Do you want to create ANOTHER booking anyway?`;
        setSubmitting(false);
        if (window.confirm(msg)) {
          // Re-submit with confirm_duplicate flag
          return submitBooking(paymentMethod, { ...opts, confirm_duplicate: true });
        }
        // Customer chose to view existing — navigate to dashboard
        navigate("/dashboard");
        return null;
      }

      sessionStorage.setItem("last_booking", JSON.stringify(data));
      // booking_id will be undefined for iter176 intent responses — that's
      // fine, we don't stash it in that branch.
      if (data.booking_id) {
        sessionStorage.setItem("last_booking_id", data.booking_id);
      }
      // iter146 — booking is now locked in the DB; the pending-estimate
      // stash is no longer needed. Clear it so the next booking starts
      // from a clean slate.
      try { sessionStorage.removeItem("jdsn_pending_estimate"); } catch { /* ignore */ }

      // iter173 — Customer Wallet auto-attach. If the customer already has
      // a saved card + AutoPay enabled, the backend automatically set
      // payment_status="autopay_scheduled" and stamped autopay_source.
      // Skip the Stripe Checkout redirect entirely — the card is already
      // on file and will auto-charge on completion. Go straight to the
      // confirmation screen.
      if (data.payment_status === "autopay_scheduled" &&
          data.autopay_source === "customer_wallet_auto_attach") {
        toast.success("Booked with your saved card on AutoPay");
        setStep(4);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return data;
      }

      // iter176 — Payment-first booking flow. If the backend held the
      // booking as an intent (requires_payment=true), redirect straight
      // to Stripe Checkout with the intent_id. NO real booking exists
      // yet; only after the webhook confirms payment will the booking
      // materialise on the admin calendar / route map.
      //
      // iter185 — intent_kind='setup' means "save card only" (no upfront
      // charge). Show the AutoPay authorization confirmation before
      // redirecting so the customer knows they're not being charged now.
      if (data.requires_payment && data.intent_id) {
        try {
          if (data.intent_kind === "setup" && data.authorization_text) {
            const proceed = window.confirm(
              `AutoPay Authorization\n\n${data.authorization_text}\n\nContinue to save your card securely?`
            );
            if (!proceed) {
              setSubmitting(false);
              return null;
            }
          }
          const { data: sess } = await api.post("/payments/checkout/session", {
            intent_id: data.intent_id,
            origin_url: window.location.origin,
            save_card_for_future: true,
            tip_amount: opts.tipAmount || 0,
            always_tip: !!opts.alwaysTip,
          });
          window.location.href = sess.url;
          return data;
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not start checkout");
          setSubmitting(false);
          return null;
        }
      }

      // Stripe: create checkout session and redirect to Stripe Checkout
      if (paymentMethod === "stripe") {
        try {
          const { data: sess } = await api.post("/payments/checkout/session", {
            booking_id: data.booking_id,
            origin_url: window.location.origin,
            save_card_for_future: true,
            tip_amount: opts.tipAmount || 0,
            always_tip: !!opts.alwaysTip,
          });
          window.location.href = sess.url;
          return data;
        } catch (err) {
          toast.error("Could not start card checkout — your booking is saved. Try again from your dashboard.");
        }
      }

      // iter84: Customer flow is Stripe-only. If Stripe redirect above
      // succeeded we've already returned; falling through here means the
      // Stripe checkout couldn't be started (booking is saved, customer
      // will be told to pay from dashboard). Advance to the confirmation
      // step so they see the booking id + next steps.
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return data;
    } catch (err) {
      // iter145 — Backend no longer rejects on price drift; the customer's
      // locked quote is always the authoritative price. Any error here is
      // a genuine failure (network, duplicate, validation), not a price mismatch.
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || "Could not book. Try again.");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2 → "Continue to payment" — validate then advance
  const continueToPayment = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.preferred_date) {
      toast.error("Please fill in all fields.");
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="book-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Book a Service</div>
      <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
        {step === 1 && "Tell us about the job"}
        {step === 2 && "Where & when?"}
        {step === 3 && "Confirm Booking & Pay"}
        {step === 4 && "Booking confirmed!"}
      </h1>
      <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        {["Estimate", "Details", "Payment", "Confirm"].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-[#047857] text-white" : done ? "bg-[#10B981] text-white" : "bg-gray-200 text-gray-600"}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              <span className={`font-semibold ${active ? "text-gray-900" : "text-gray-500"}`}>{label}</span>
              {i < 3 && <div className="w-6 sm:w-8 h-px bg-gray-300 mx-1" />}
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        {step === 1 && (
          <EstimateCalculator services={services} defaultService={defaultService} defaultFrequency={defaultFrequency} defaultYardArea={defaultYardArea} defaultSqft={defaultSqft} compact onContinue={handleContinue} />
        )}

        {step === 2 && estimate && (
          <form onSubmit={continueToPayment} className="grid lg:grid-cols-3 gap-6" data-testid="booking-form">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 space-y-5">
              {!user && (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-3" data-testid="book-google-prompt">
                  <div className="w-9 h-9 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.4-.1-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.6 39.6 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C41.8 35 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-extrabold text-gray-900 leading-tight">Sign in for faster booking</div>
                    <div className="text-[11px] text-gray-600 leading-snug">We'll auto-fill your details and save this booking to your account.</div>
                  </div>
                  <Link to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="h-9 px-3 rounded-full text-xs font-bold bg-[#062818] text-white flex items-center" data-testid="book-google-cta">
                    Continue with Google →
                  </Link>
                </div>
              )}
              {/* iter255 — Property picker for signed-in customers with
                  ≥1 saved property. Selecting a property pre-fills the
                  address + sqft + yard area with THAT property's data so
                  pricing is computed for the selected property only. */}
              {user && properties.length > 0 && (
                <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-3 sm:p-4"
                     data-testid="book-property-picker">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#047857]">
                      Which property is this for?
                    </div>
                    <Link to="/dashboard#properties" className="text-[11px] font-extrabold text-[#047857] underline">
                      + Add another property
                    </Link>
                  </div>
                  <div className="mt-2 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {properties.map((p) => {
                      const sel = selectedPropertyId === p.property_id;
                      return (
                        <button
                          key={p.property_id}
                          type="button"
                          onClick={() => chooseProperty(p)}
                          className={`flex-shrink-0 min-w-[180px] text-left rounded-xl border-2 px-3 py-2.5 transition ${
                            sel
                              ? "border-[#047857] bg-white shadow-sm"
                              : "border-transparent bg-white/70 hover:border-emerald-300"
                          }`}
                          data-testid={`book-property-${p.property_id}`}
                        >
                          <div className="text-[13px] font-extrabold text-gray-900 truncate">
                            {p.label || "Property"}
                            {sel && <span className="ml-1.5 text-[#047857]">✓</span>}
                          </div>
                          <div className="text-[11px] text-gray-600 truncate">{p.address}</div>
                          <div className="mt-0.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                            {(p.square_feet || 0).toLocaleString()} sq ft
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} required className="h-12 mt-1.5" data-testid="book-name" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} required className="h-12 mt-1.5" data-testid="book-phone" />
                  {/* iter150 — Twilio A2P 10DLC consent language + inline
                      opt-in checkbox. Not a hard blocker (customers who
                      prefer email-only can uncheck) but the default is
                      opt-in since they entered the number to be reached.
                      Consent state is included in the booking payload. */}
                  <label className="mt-2 flex items-start gap-2 cursor-pointer select-none" data-testid="book-sms-consent-label">
                    <input
                      type="checkbox"
                      checked={form.sms_consent !== false}
                      onChange={(e) => setField("sms_consent", e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#047857]"
                      data-testid="book-sms-consent"
                    />
                    <span className="text-[11px] leading-snug text-gray-600">
                      Yes, text me service updates at this number (confirmations,
                      reminders, receipts). Message frequency varies. Message and data
                      rates may apply. Reply <strong>STOP</strong> to unsubscribe,
                      <strong> HELP</strong> for help. See our{" "}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                         className="text-[#047857] font-bold underline">Privacy Policy</a>{" "}
                      and{" "}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer"
                         className="text-[#047857] font-bold underline">Terms</a>.
                    </span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} required className="h-12 mt-1.5" data-testid="book-email" />
                </div>
                <div className="sm:col-span-2">
                  <AddressAutocomplete
                    id="address"
                    value={form.address}
                    onChange={(v) => setField("address", v)}
                    onPlace={(p) => {
                      setField("address", p.formatted_address || p.address || form.address);
                      const f = (p.formatted_address || "").toLowerCase();
                      const hit = SERVICE_AREAS.find((a) => f.includes(a.toLowerCase()));
                      if (hit) setField("area", hit);
                      // iter89 — Only recalculate the property size + price
                      // when the SERVICE ADDRESS actually changes. Google
                      // Places autocomplete can re-fire onPlace during
                      // step-2 form typing / blur events — those re-fires
                      // must NOT shift the price while the customer is
                      // filling in name/phone/email/date/notes.
                      const prevAddr = (property?.formatted_address || "").trim().toLowerCase();
                      const nextAddr = (p?.formatted_address || "").trim().toLowerCase();
                      const addressChanged = !!nextAddr && nextAddr !== prevAddr;
                      if (addressChanged) {
                        setProperty(p);
                        // Use MOWABLE area (lot − house − non-grass) when
                        // available, fall back to raw lot size.
                        const detected = Number(p?.mowable_sqft || p?.square_feet || 0);
                        if (detected > 0) recalcWithSqft(detected);
                      }
                      // NOTE: property state is intentionally NOT updated
                      // on re-fires with the same address — this keeps the
                      // satellite preview stable and prevents any downstream
                      // effect from re-running.
                    }}
                    testId="book-address"
                  />
                  {/* iter43 — Customer-only satellite property preview */}
                  {!user?.is_admin && <CustomerPropertyPreview property={property} />}
                  {/* iter42 — Customers see NO size data here. Admins still get
                      the verification context for transparency. */}
                  {!property?.square_feet && user?.is_admin && (
                    <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="lot-pending-message">
                      Property size requires manual review. Final price may be adjusted after verification.
                    </div>
                  )}
                  {/* iter89 — Explicit "Recalculate Property" action. Small
                      and out-of-the-way — visible to both customer and admin
                      per spec ("customer can click a Recalculate Property
                      button"). Re-queries the parcel API for the current
                      formatted address and refreshes sqft + price. */}
                  {property?.formatted_address && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!property?.lat || !property?.lng) {
                          toast.error("No property location on file — please re-enter your address.");
                          return;
                        }
                        try {
                          const { data } = await api.post("/property/lookup", {
                            lat: Number(property.lat),
                            lng: Number(property.lng),
                            address: property.formatted_address || "",
                          });
                          if (data?.formatted_address || data?.square_feet) {
                            const merged = { ...property, ...data };
                            setProperty(merged);
                            const detected = Number(data?.mowable_sqft || data?.square_feet || 0);
                            if (detected > 0) recalcWithSqft(detected);
                            toast.success("Property refreshed");
                          } else {
                            toast.info("No new parcel data was found");
                          }
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Could not refresh property");
                        }
                      }}
                      className="mt-2 text-[11px] font-extrabold text-[#047857] hover:text-[#065F46] underline underline-offset-2"
                      data-testid="recalculate-property-btn"
                    >
                      Recalculate property
                    </button>
                  )}
                </div>
                <div>
                  <Label>Neighborhood</Label>
                  <Select value={form.area} onValueChange={(v) => setField("area", v)}>
                    <SelectTrigger className="h-12 mt-1.5" data-testid="book-area"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Preferred date</Label>
                  <Input id="date" type="date" value={form.preferred_date} onChange={(e) => setField("preferred_date", e.target.value)} required className="h-12 mt-1.5" data-testid="book-date" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Preferred time</Label>
                  <Select value={form.preferred_time} onValueChange={(v) => setField("preferred_time", v)}>
                    <SelectTrigger className="h-12 mt-1.5" data-testid="book-time"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea id="notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1.5 min-h-24" data-testid="book-notes" placeholder="Gate code, dogs in yard, specific instructions…" />
                </div>
                {/* iter135 — Gate width required so the crew knows backyard access */}
                <div className="sm:col-span-2 mt-1">
                  <GateWidthPicker
                    value={estimate.gate_width || ""}
                    onChange={(v) => setEstimate((e) => ({ ...e, gate_width: v }))}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="rounded-full h-12 px-6">Back</Button>
                <Button
                  type="submit"
                  disabled={submitting || !(estimate?.calc?.total > 0) || !estimate?.gate_width}
                  className="rounded-full h-12 px-7 bg-[#047857] hover:bg-[#065F46] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="book-submit-btn"
                  title={
                    !(estimate?.calc?.total > 0)
                      ? "Waiting for your custom price to finish calculating…"
                      : !estimate?.gate_width
                        ? "Please select your gate width first"
                        : ""
                  }
                >
                  {submitting
                    ? "Processing…"
                    : !(estimate?.calc?.total > 0)
                      ? "Calculating price…"
                      : !estimate?.gate_width
                        ? "Select gate width"
                        : "Continue to payment →"}
                </Button>
              </div>
            </div>
            <aside className="bg-white rounded-2xl border border-gray-200 p-6 h-fit lg:sticky lg:top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Order summary</div>
              <div className="mt-3 text-xl font-bold text-gray-900">{estimate.calc.service_name}</div>
              {user?.is_admin ? (
                <div className="text-sm text-gray-600" data-testid="summary-yard-line">
                  {estimate.sqft.toLocaleString()} sqft · {estimate.calc.yard_area_label || "Full yard"}
                </div>
              ) : (
                <div className="text-sm text-gray-600" data-testid="summary-arrival-line">
                  {estimate.calc.frequency_label || "Standard"} · {form.preferred_time || "Morning (7am – 11am)"} arrival window
                </div>
              )}

              {/* If address is geocoded but parcel data wasn't found — admin only override path */}
              {property?.lat && !property?.acres && user?.is_admin && (
                <div className="mt-4">
                  <YardSizeSelector selectedSqft={estimate.sqft} onSelect={recalcWithSqft} />
                </div>
              )}

              {/* Property summary chip — pure data, no extra Google API needed */}
              {property?.lat && property?.lng && (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="property-map">
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-white border border-emerald-200 flex items-center justify-center text-[#047857] flex-shrink-0">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">Property</div>
                      <div className="text-xs font-bold text-gray-900 mt-0.5 leading-snug break-words">{property.formatted_address}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-bold">
                        {/* iter42 — Customers only see the verified pin; no acreage, sqft, or category chips. */}
                        {property.acres && user?.is_admin && <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[#047857]">~{property.acres} ac</span>}
                        {property.square_feet && user?.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[#047857]" data-testid="admin-sqft-chip">
                            ~{property.square_feet.toLocaleString()} sqft
                          </span>
                        )}
                        {!user?.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[#047857]" data-testid="customer-property-verified-chip">
                            Property verified
                          </span>
                        )}
                        {!property.acres && user?.is_admin && <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500">Lat {property.lat.toFixed(4)}, {property.lng.toFixed(4)}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {estimate.calc.yard_area_label && (
                  <span className={`inline-flex items-center text-[11px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${estimate.calc.yard_area_factor < 1 ? "bg-emerald-100 text-[#047857]" : "bg-gray-100 text-gray-700"}`} data-testid="summary-yard-pill">
                    {estimate.calc.yard_area_label}
                  </span>
                )}
                {estimate.calc.frequency_label && (
                  <span className={`inline-flex items-center text-[11px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${estimate.calc.frequency_delta < 0 ? "bg-emerald-100 text-[#047857]" : estimate.calc.frequency_delta > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"}`} data-testid="summary-freq-pill">
                    {estimate.calc.frequency_label}
                  </span>
                )}
              </div>
              {/* Pricing breakdown — admins only. Customers see only the final total. */}
              {user?.is_admin && (
                <div className="mt-4 border-t border-gray-100 pt-4 space-y-1.5" data-testid="book-breakdown">
                  {estimate.calc.breakdown.map((b, i) => (
                    <div key={i} className="flex justify-between text-sm text-gray-700"><span>{b.label}</span><span className="font-semibold">${b.amount.toFixed(2)}</span></div>
                  ))}
                </div>
              )}

              {/* Additional services (multi-service booking) — iter116:
                  prominent, full-width Add More Services CTA before the
                  total + payment CTA. This is the ONLY place in checkout
                  to add extra services. */}
              <div className="mt-4 border-t border-gray-100 pt-4" data-testid="book-order-summary-addons">
                <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-2">Additional services</div>
                {additional.length === 0 ? (
                  <div className="text-[12px] text-gray-500 italic mb-3">
                    None yet — add hedge trimming, mulching, pressure washing and more to the same visit.
                  </div>
                ) : (
                  <ul className="space-y-2 mb-3" data-testid="addons-list">
                    {additional.map((a, idx) => (
                      <li key={`${a.service_key}-${idx}`} className="rounded-xl border border-gray-200 bg-white px-3 py-2" data-testid={`addon-row-${a.service_key}`}>
                        <div className="flex items-start gap-2">
                          <Leaf className="w-3.5 h-3.5 text-[#10B981] mt-1 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-extrabold tracking-tight text-gray-900 truncate">{a.service_name}</div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {a.label || (a.square_feet ? `${a.square_feet.toLocaleString()} sqft` : "Quote estimate")}
                              {a.frequency && a.frequency !== "one_time" && (
                                <> · <span className="font-bold text-emerald-800">{
                                  {
                                    weekly:"Weekly", biweekly:"Every 2 wks", every_3_weeks:"Every 3 wks",
                                    every_4_weeks:"Every 4 wks", monthly:"Monthly", every_6_weeks:"Every 6 wks",
                                    every_2_months:"Every 2 mo", every_3_months:"Every 3 mo",
                                    every_6_months:"Every 6 mo", yearly:"Yearly", custom:"Custom",
                                  }[a.frequency] || a.frequency
                                }</span></>
                              )}
                              {a.schedule?.start_date && a.frequency !== "one_time" && (
                                <> · Starts {a.schedule.start_date}</>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-extrabold tabular-nums text-gray-900">${a.price.toFixed(2)}</div>
                            <button
                              type="button"
                              onClick={() => setAdditional((prev) => prev.filter((_, i) => i !== idx))}
                              className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 hover:underline"
                              data-testid={`addon-remove-${a.service_key}`}
                            >
                              <Trash2 className="w-2.5 h-2.5" /> Remove
                            </button>
                          </div>
                        </div>
                        {/* iter107 — itemized advanced-addon line items */}
                        {a.line_items && a.line_items.length > 0 && (
                          <ul className="mt-2 pl-5 space-y-0.5 text-[11px] text-gray-600 border-l border-emerald-100" data-testid={`addon-lineitems-${a.service_key}`}>
                            {a.line_items.map((li, li_idx) => (
                              <li key={li_idx} className="flex items-center justify-between gap-2">
                                <span className="truncate">{li.label}</span>
                                <span className="tabular-nums font-bold text-gray-700 shrink-0">
                                  {li.amount > 0 ? `+$${li.amount.toFixed(2)}` : "included"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  data-testid="add-service-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-50 hover:bg-emerald-100 active:scale-[0.99] border-2 border-dashed border-emerald-300 text-[#047857] font-extrabold py-3.5 text-sm uppercase tracking-[0.12em] transition"
                >
                  <Plus className="w-4 h-4" /> Add More Services
                </button>
              </div>

              <div className="mt-4 border-t border-gray-100 pt-4 flex items-center justify-between">
                <span className="font-bold text-gray-900">Total</span>
                <span className="text-2xl font-extrabold text-[#047857] tabular-nums" data-testid="step2-total">${(estimate.calc.total + additional.reduce((s, a) => s + (a.price || 0), 0)).toFixed(2)}</span>
              </div>
              <div className="mt-4 text-[11px] text-gray-500 leading-relaxed">Secure card checkout powered by Stripe. Save your card for one-tap rebooking.</div>
            </aside>
          </form>
        )}

        {step === 3 && estimate && (
          <>
            {/* iter234 (2026-03-02) — "Add More Services" panel visible on the
                Confirm Booking page, BEFORE the Pay button. Every add-on is
                attached to the SAME booking; total updates immediately; tip is
                recalculated inside PaymentMethodStep because we always pass
                the fresh combined amount. */}
            <div className="max-w-3xl mx-auto mb-6 bg-white border border-emerald-100 rounded-3xl p-5 sm:p-6 shadow-sm"
                  data-testid="confirm-add-more-services">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-emerald-800">
                    Your Services
                  </div>
                  <div className="mt-0.5 text-xl font-extrabold text-gray-900 font-[Outfit]">
                    Add More Services
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                    Services subtotal
                  </div>
                  <div className="text-2xl font-extrabold text-emerald-700 tabular-nums"
                        data-testid="confirm-services-subtotal">
                    ${(estimate.calc.total + additional.reduce((s, a) => s + (a.price || 0), 0)).toFixed(2)}
                  </div>
                  <div className="text-[10px] text-gray-500 font-medium mt-0.5">
                    Tip &amp; grand total below
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2" data-testid="confirm-service-list">
                {/* Main service row */}
                <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-800">
                      Main service
                    </div>
                    <div className="text-sm font-extrabold text-gray-900 truncate">
                      {estimate.calc.service_name}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {form.preferred_date || "Date on request"} · {freqLabel(estimate.frequency)}
                    </div>
                  </div>
                  <div className="text-lg font-extrabold text-emerald-700 tabular-nums"
                        data-testid="confirm-main-price">
                    ${estimate.calc.total.toFixed(2)}
                  </div>
                </div>

                {/* Add-on rows */}
                {additional.map((a, idx) => (
                  <div key={a.service_key + idx}
                        data-testid={`confirm-addon-row-${a.service_key}`}
                        className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <Leaf className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-extrabold text-gray-900 truncate" data-testid={`confirm-addon-name-${a.service_key}`}>
                        {a.service_name || a.service_key.replace(/_/g, " ")}
                      </div>
                      {a.label && (
                        <div className="text-[11px] text-gray-500 truncate">
                          {a.label}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500">
                        Add-on · {freqLabel(a.frequency || estimate.frequency)}
                        {a.schedule?.start_date && a.frequency && a.frequency !== "one_time" && (
                          <> · Starts {a.schedule.start_date}</>
                        )}
                      </div>
                    </div>
                    <div className="text-base font-extrabold text-emerald-700 tabular-nums">
                      ${Number(a.price || 0).toFixed(2)}
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdditional((prev) => prev.filter((_, i) => i !== idx))}
                      data-testid={`confirm-addon-remove-${a.service_key}`}
                      className="w-8 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center flex-shrink-0"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setAddOpen(true)}
                data-testid="confirm-add-service-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-50 hover:bg-emerald-100 active:scale-[0.99] border-2 border-dashed border-emerald-300 text-[#047857] font-extrabold py-3.5 text-sm uppercase tracking-[0.12em] transition"
              >
                <Plus className="w-4 h-4" /> Add another service
              </button>
              <p className="mt-2 text-[11px] text-gray-500 text-center">
                All services stay on the same booking. Tip and total update
                automatically when you add or remove.
              </p>
            </div>

            <PaymentMethodStep
              amount={estimate.calc.total + additional.reduce((s, a) => s + (a.price || 0), 0)}
              bookingNote={`${estimate.calc.service_name}${additional.length ? ` + ${additional.length} more` : ""} · ${form.preferred_date}`}
              submitting={submitting}
              onBack={() => setStep(2)}
              onConfirm={submitBooking}
              wallet={wallet}
            />
          </>
        )}

        {step === 4 && (
          <div className="bg-white border border-gray-200 rounded-3xl p-7 sm:p-10 max-w-2xl mx-auto" data-testid="booking-success">
            {(() => {
              const last = (() => { try { return JSON.parse(sessionStorage.getItem("last_booking") || "null"); } catch { return null; } })();
              const isPendingVerify = last?.status === "pending_payment_verification";
              const isCash = last?.payment_status === "pay_on_arrival";
              const Icon = isPendingVerify ? Clock : (isCash ? Banknote : CheckCircle2);
              const iconBg = isPendingVerify ? "bg-amber-100" : "bg-emerald-100";
              const iconCol = isPendingVerify ? "text-amber-600" : "text-[#10B981]";
              const headline = isPendingVerify ? "Almost there!" : "You're booked!";
              const pillText = isPendingVerify ? "Pending Payment Verification" : (isCash ? "Reserved" : "Confirmed");
              const pillBg = isPendingVerify ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-[#047857] border-emerald-200";
              return (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${iconCol}`} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Booking #{(last?.booking_id || "").slice(-6)}</div>
                      <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">{headline}</h2>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 mt-1 rounded-full text-[11px] uppercase tracking-[0.16em] font-extrabold border ${pillBg}`} data-testid="booking-status-pill">
                    {pillText}
                  </span>
                  <p className="mt-3 text-gray-700 text-sm">
                    {isPendingVerify
                      ? <>We received your <span className="font-bold">{last?.payment_method_label}</span> payment notice. Judson will verify the funds shortly and you'll get a confirmation. We'll see you on <span className="font-bold">{form.preferred_date}</span> ({form.preferred_time}).</>
                      : isCash
                        ? <>Bring <span className="font-bold">${(estimate.calc.total + additional.reduce((s, a) => s + (a.price || 0), 0)).toFixed(2)} cash</span> on the day. We'll see you on <span className="font-bold">{form.preferred_date}</span> ({form.preferred_time}).</>
                        : <>A confirmation has been sent. We'll see you on <span className="font-bold">{form.preferred_date}</span> ({form.preferred_time}).</>}
                  </p>
                </>
              );
            })()}

            {estimate && (
              <div className="mt-5 rounded-2xl bg-[#F9FAFB] border border-gray-200 p-4 sm:p-5 text-sm" data-testid="confirm-services-block">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Services in this booking</div>
                <ul className="space-y-1.5 mb-3" data-testid="confirm-services-list">
                  <li className="flex items-center justify-between">
                    <span className="font-bold text-gray-900">{estimate.calc.service_name}</span>
                    <span className="font-extrabold tabular-nums text-gray-900">${estimate.calc.total.toFixed(2)}</span>
                  </li>
                  {additional.map((a, i) => (
                    <li key={i} className="flex items-center justify-between text-gray-800" data-testid={`confirm-addon-${a.service_key}`}>
                      <span><span className="text-[#047857] mr-1">+</span>{a.service_name}{a.label ? <span className="text-gray-500"> · {a.label}</span> : null}</span>
                      <span className="font-extrabold tabular-nums">${a.price.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-3">
                  <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Total</div><div className="font-extrabold text-[#047857] tabular-nums text-lg">${(estimate.calc.total + additional.reduce((s, a) => s + (a.price || 0), 0)).toFixed(2)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Yard area</div><div className="font-bold text-gray-900">{estimate.calc.yard_area_label}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Frequency</div><div className="font-bold text-gray-900">{estimate.calc.frequency_label}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Square feet</div><div className="font-bold text-gray-900">{estimate.sqft.toLocaleString()}</div></div>
                  <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Address</div><div className="font-bold text-gray-900">{property?.formatted_address || `${form.address}, ${form.area}`}</div></div>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2.5">
              <a href={`tel:${BRAND.phoneTel}`} className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-gray-200 text-gray-800 font-semibold hover:bg-gray-50" data-testid="confirm-call-btn">
                <Phone className="w-4 h-4 text-[#047857]" /> {BRAND.phone}
              </a>
              <Link to="/dashboard"><Button variant="outline" className="rounded-full h-12 px-5" data-testid="confirm-dashboard-btn">View bookings</Button></Link>
            </div>
            <p className="mt-4 text-[11px] text-gray-500">Check your email for a copy of this confirmation. Need to make a change? Text <span className="font-bold">{BRAND.phone}</span>.</p>
          </div>
        )}
      </div>
      <AddServiceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        services={services}
        excludeKeys={[estimate?.serviceKey, ...additional.map((a) => a.service_key)].filter(Boolean)}
        defaultSqft={estimate?.sqft || 2000}
        defaultStartDate={form.preferred_date || ""}
        onAdd={(item) => setAdditional((prev) => [...prev, item])}
      />
      {/* iter103 — trust signals row visible on booking flow */}
      <TrustSignalsBar />
    </div>
  );
}
